import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  fetchDuplicates,
  fetchAssetsByCategory,
  postRequestSignedUrls
} from '../../../assets/requests'
import { useUserInfo } from '../../Authentication/hooks'
import {
  createNavigationHandlers,
  sortAssetsByDate,
  removeAssetFromArray,
  formatAssetDate,
  createModalProps,
  handleAsyncError
} from './utils/categoryUtils'
import { useAssetDeletion } from '../AssetActions/hooks/useAssetDeletion'
import { deleteAsset, restoreAsset } from '../../../assets/requests'
import { toast } from 'react-toastify'
import placeholderImg from '../../../assets/images/camera.jpg'
import deleteImg from '../../../assets/images/delete2.svg'
import '../../../assets/stylesheets/gallery-style.css'
import '../../../assets/stylesheets/assets.css'
import '../../../assets/stylesheets/duplicates.css'
import '../../../assets/stylesheets/modal.css'
import { handleModalOpen } from '../Modal/modals'
import { ModalHtml } from '../Modal/modals'
import upArrow from '../../../assets/images/up-arrow.png'
import backArrow from '../../../assets/images/back-arrow3.png'
import homeIcon from '../../../assets/images/home-icon.png'

const DuplicatesCategory = () => {
  const [categoryDetails, setCategoryDetails] = useState(null)
  const [modalActive, setModalActive] = useState(false)
  const [currentAssetId, setCurrentAssetId] = useState(null)
  const [assets, setAssets] = useState([])
  const [refreshTrigger, setRefreshTrigger] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedAssets, setSelectedAssets] = useState(new Set())
  const [selectionMode, setSelectionMode] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const dispatch = useDispatch()
  const { userId } = useUserInfo()
  const navigate = useNavigate()
  const { deleteAssetById } = useAssetDeletion()

  const { handleHomeClick, handleBackClick, handleBackToTop } =
    createNavigationHandlers(navigate)

  const signAssets = useCallback(
    async assets => {
      if (!userId) {
        return assets
      }

      let assetIds = Object.keys(assets)
      let thumbnailKeys = []
      let s3Uris = []
      let s3UriIdx = []

      assetIds.forEach((id, idx) => {
        const asset = assets[id]
        if (!asset.thumbnailUrl && asset.thumbnails && asset.thumbnails.length > 0) {
          thumbnailKeys.push(asset.thumbnails[1])
        }

        if (!asset.fileUrl) {
          s3Uris.push(asset.s3uri)
          s3UriIdx.push(idx)
        }
      })

      const [signedThumbnailUrls, signedS3Uris] = await Promise.all([
        thumbnailKeys.length > 0
          ? postRequestSignedUrls({ userId, payload: thumbnailKeys })
          : [],
        s3Uris.length > 0
          ? postRequestSignedUrls({ userId, payload: s3Uris })
          : []
      ])

      assetIds.forEach((id, idx) => {
        if (signedThumbnailUrls[idx]) {
          assets[id].thumbnailUrl = signedThumbnailUrls[idx]
        }

        const s3UriSignedIndex = s3UriIdx.indexOf(idx)
        if (s3UriSignedIndex !== -1 && signedS3Uris && signedS3Uris[s3UriSignedIndex]) {
          assets[id].fileUrl = signedS3Uris[s3UriSignedIndex]
        }
      })

      return assets
    },
    [userId]
  )

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError('')
        const details = await fetchDuplicates()
        setCategoryDetails(details)
        if (details) {
          const rawAssets = await fetchAssetsByCategory({
            categoryId: details.id,
            userId: userId
          })
          const signedAssets = await signAssets(rawAssets)
          const sortedAssets = sortAssetsByDate(signedAssets, 'asc')

          setAssets(sortedAssets)
        }
      } catch (error) {
        const errorMessage = handleAsyncError(error, 'fetching assets')
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [userId, signAssets, refreshTrigger])

  const handleDeleteSingle = useCallback(async (assetId) => {
    const success = await deleteAssetById(assetId, userId)
    if (success) {
      setAssets(prev => removeAssetFromArray(prev, assetId))
    }
  }, [deleteAssetById, userId])

  const handleSelectAll = () => {
    if (!selectionMode) {
      setSelectionMode(true)
      const allIds = Array.isArray(assets)
        ? assets.map(a => a.id)
        : Object.keys(assets)
      setSelectedAssets(new Set(allIds))
    } else {
      setSelectionMode(false)
      setSelectedAssets(new Set())
    }
  }

  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => {
      const next = new Set(prev)
      if (next.has(assetId)) {
        next.delete(assetId)
      } else {
        next.add(assetId)
      }
      return next
    })
  }

  const handleDeleteSelected = async () => {
    if (selectedAssets.size === 0) return
    setDeleting(true)
    const idsToDelete = [...selectedAssets]
    const deletedCount = idsToDelete.length

    // Delete all selected assets without individual toasts
    const results = await Promise.allSettled(
      idsToDelete.map(assetId =>
        deleteAsset({ userId, assetId })
      )
    )

    const successIds = idsToDelete.filter((_, i) => results[i].status === 'fulfilled')
    const failCount = deletedCount - successIds.length

    // Show one combined toast with undo for all
    if (successIds.length > 0) {
      const handleUndoAll = async () => {
        try {
          await Promise.allSettled(
            successIds.map(assetId => restoreAsset({ userId, assetId }))
          )
          toast.success(`${successIds.length} photo(s) restored!`, {
            autoClose: 1000,
            hideProgressBar: true
          })
          setRefreshTrigger(prev => !prev)
        } catch (err) {
          toast.error('Failed to restore photos.', {
            autoClose: 2000,
            hideProgressBar: true
          })
        }
      }

      toast.info(
        ({ closeToast }) => (
          <div>
            {successIds.length} photo(s) deleted.{' '}
            <button
              onClick={() => { handleUndoAll(); closeToast(); }}
              style={{
                background: 'none',
                border: 'none',
                color: '#3498db',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: 'inherit',
                padding: 0
              }}
            >
              Undo
            </button>
          </div>
        ),
        { autoClose: 5000, hideProgressBar: false }
      )
    }

    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} photo(s).`, {
        autoClose: 2000,
        hideProgressBar: true
      })
    }

    setSelectionMode(false)
    setSelectedAssets(new Set())
    setDeleting(false)
    if (successIds.length > 0) {
      setRefreshTrigger(prev => !prev)
    }
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>
  }

  if (loading) {
    return <div>Loading...</div>
  }

  if (!categoryDetails || !assets.length) {
    return (
      <div className="duplicates-category-container">
        <h2 className="row-title-duplicates">Duplicates</h2>
        <div className="go-back">
          <img className="nav-icon-home home fav" alt="Go to base of navigation" src={homeIcon} onClick={handleHomeClick} />
          <img className="nav-icon-back back" alt="Go back one level." src={backArrow} onClick={handleBackClick} />
          <img className="nav-icon-top top" alt="Go back to top." src={upArrow} onClick={handleBackToTop} />
        </div>
        <div className="gallery-container">
          <div className="empty-state-message">
            <p>No duplicate images found in your collection.</p>
          </div>
        </div>
      </div>
    )
  }

  const closeModal = () => {
    setModalActive(false)
  }

  const handleAssetDeleted = deletedAssetId => {
    const updatedAssets = removeAssetFromArray(assets, deletedAssetId)
    setAssets(updatedAssets)
    closeModal()
    setRefreshTrigger(!refreshTrigger)
  }

  return (
    <div className="duplicates-category-container">
      <h2 className="row-title-duplicates">Duplicates</h2>
      <div className="go-back">
        <img className="nav-icon-home home fav" alt="Go to base of navigation" src={homeIcon} onClick={handleHomeClick} />
        <img className="nav-icon-back back" alt="Go back one level." src={backArrow} onClick={handleBackClick} />
        <img className="nav-icon-top top" alt="Go back to top." src={upArrow} onClick={handleBackToTop} />
      </div>

      <div className="bulk-action-bar">
        <button className="btn-select-all" onClick={handleSelectAll}>
          {selectionMode ? 'Cancel' : 'Select All'}
        </button>
        {selectionMode && (
          <>
            <span className="selection-count">{selectedAssets.size} selected</span>
            <button
              className="btn-delete-selected"
              onClick={handleDeleteSelected}
              disabled={deleting || selectedAssets.size === 0}
            >
              {deleting ? 'Deleting...' : `Delete Selected (${selectedAssets.size})`}
            </button>
          </>
        )}
      </div>

      <div className="gallery-container">
        {assets.map(asset => (
          <div
            key={asset.id}
            className={`gallery-thumb-duplicates ${selectionMode ? (selectedAssets.has(asset.id) ? 'selected' : 'deselected') : ''}`}
          >
            <div
              className="gallery-thumb2"
              style={{
                backgroundImage: `url('${asset.thumbnailUrl || placeholderImg}')`
              }}
              onClick={() => {
                if (selectionMode) {
                  return
                }
                setCurrentAssetId(asset.id)
                const assetsMap = Object.fromEntries(assets.map(a => [a.id, a]))
                const modalProps = createModalProps(
                  dispatch,
                  modalActive,
                  assetsMap,
                  asset.id
                )
                handleModalOpen(modalProps)
                setModalActive(true)
              }}
            >
              {selectionMode && selectedAssets.has(asset.id) && (
                <button
                  className="thumb-deselect-btn"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleAssetSelection(asset.id)
                  }}
                  title="Keep this photo"
                >
                  ✕
                </button>
              )}
              {!selectionMode && (
                <button
                  className="thumb-action-overlay"
                  onClick={(e) => { e.stopPropagation(); handleDeleteSingle(asset.id) }}
                  title="Delete"
                >
                  <img src={deleteImg} alt="Delete" />
                </button>
              )}
              <div className="duplicates-date">
                {formatAssetDate(asset.content?.date)}
              </div>
            </div>
          </div>
        ))}
      </div>
      {modalActive && (
        <ModalHtml
          state={{
            modalActive,
            currentAsset: currentAssetId,
            modalStatus: 'Image',
            assets,
            publishedAssets: Object.fromEntries(assets.map(a => [a.id, a]))
          }}
          onAssetDeleted={handleAssetDeleted}
          dispatch={dispatch}
          onClose={closeModal}
        />
      )}
    </div>
  )
}

export default DuplicatesCategory
